import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type Owner from '@ember/owner';

export type ThemeMode = 'light' | 'dark';
export type StoredMode = ThemeMode | 'system';

const STORAGE_KEY = 'tio.mode';
const LEGACY_STORAGE_KEY = 'theme';

function isStoredMode(value: unknown): value is StoredMode {
  return value === 'light' || value === 'dark' || value === 'system';
}

function readStoredMode(): StoredMode {
  try {
    const raw = localStorage.getItem(STORAGE_KEY) ?? localStorage.getItem(LEGACY_STORAGE_KEY);
    return isStoredMode(raw) ? raw : 'system';
  } catch {
    return 'system';
  }
}

function writeStoredMode(stored: StoredMode) {
  try {
    if (stored === 'system') {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, stored);
      localStorage.setItem(LEGACY_STORAGE_KEY, stored);
    }
  } catch {
    // The in-memory preference still applies when storage is unavailable.
  }
}

function systemPrefersDark() {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}

function resolveMode(stored: StoredMode): ThemeMode {
  if (stored === 'system') {
    return systemPrefersDark() ? 'dark' : 'light';
  }
  return stored;
}

function applyMode(stored: StoredMode, resolved: ThemeMode) {
  const html = document.documentElement;
  html.setAttribute('data-mode', resolved);
  html.setAttribute('data-stored-mode', stored);
  html.classList.toggle('dark', resolved === 'dark');
}

/**
 * Owns the persisted light, dark, or system preference and keeps the canonical
 * `data-mode` attribute plus the compatibility `.dark` selector synchronized.
 */
export default class ThemeService extends Service {
  @tracked storedMode: StoredMode = 'system';
  @tracked resolvedMode: ThemeMode = 'light';

  #mediaQuery: MediaQueryList | null = null;
  #handleSystemChange = (event: MediaQueryListEvent) => {
    if (this.storedMode !== 'system') return;
    this.resolvedMode = event.matches ? 'dark' : 'light';
    applyMode(this.storedMode, this.resolvedMode);
  };

  constructor(owner: Owner) {
    super(owner);
    this.storedMode = readStoredMode();
    this.resolvedMode = resolveMode(this.storedMode);
    applyMode(this.storedMode, this.resolvedMode);

    try {
      this.#mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      this.#mediaQuery.addEventListener('change', this.#handleSystemChange);
    } catch {
      this.#mediaQuery = null;
    }
  }

  /**
   * Applies and persists a theme preference for the current browser.
   *
   * @param stored - Explicit light/dark choice, or system to follow the OS.
   */
  setMode(stored: StoredMode) {
    this.storedMode = stored;
    this.resolvedMode = resolveMode(stored);
    writeStoredMode(stored);
    applyMode(this.storedMode, this.resolvedMode);
  }

  willDestroy() {
    this.#mediaQuery?.removeEventListener('change', this.#handleSystemChange);
    this.#mediaQuery = null;
    super.willDestroy();
  }
}

declare module '@ember/service' {
  interface Registry {
    theme: ThemeService;
  }
}
